// src/lib/repairs/types.ts
//
// Repairs / service-tickets module (M19 + Phase 5 wp11).
// Vendor-agnostic: works for in-house + vendor-routed tickets.
// Pairs with the Agent H graph defined in
// docs/proposals/payment-terms-repairs-importer.md §4.
//
// All persistence is in lib/repairs/store.ts. The store wires this
// type into React Context.

export type RepairStatus =
  | "open"
  | "triage"
  | "vendor-routing"
  | "in-repair"
  | "return-shipped"
  | "closed";

export const REPAIR_STATUS_LABEL: Record<RepairStatus, string> = {
  open: "Open",
  triage: "Triage",
  "vendor-routing": "Vendor routing",
  "in-repair": "In repair",
  "return-shipped": "Returned to customer",
  closed: "Closed",
};

/** Total ordering used for the progress rail. */
export const REPAIR_STATUS_ORDER: RepairStatus[] = [
  "open",
  "triage",
  "vendor-routing",
  "in-repair",
  "return-shipped",
  "closed",
];

export type RepairWarranty =
  | "in-warranty"
  | "extended-warranty"
  | "out-of-warranty";

export const WARRANTY_LABEL: Record<RepairWarranty, string> = {
  "in-warranty": "Under vendor warranty",
  "extended-warranty": "BENCHMARK extended warranty",
  "out-of-warranty": "Out of warranty",
};

export interface RepairTimelineEntry {
  ts: string;
  status: RepairStatus;
  actor: "system" | "agent" | "human";
  note: string;
}

export interface RepairTicket {
  id: string;
  customerId: string;
  customerName: string;
  sourceOrderId?: string;
  sku: string;
  productName: string;
  serialNumber?: string;
  description: string;
  warranty: RepairWarranty;
  status: RepairStatus;
  /** Vendor the BENCHMARK service desk is routing to (when in vendor-routing). */
  vendor?: string;
  /** GLS return label (when in return-shipped). */
  glsTrackingNumber?: string;
  /** Operator-owned free-text note for the service team. */
  internalNotes: string;
  timeline: RepairTimelineEntry[];
}

export const STATUS_TONE: Record<RepairStatus, string> = {
  open: "bg-bg-elevated text-fg-secondary",
  triage: "bg-accent-info/15 text-accent-info",
  "vendor-routing": "bg-accent-indigo/15 text-accent-indigo",
  "in-repair": "bg-benchmark-teal/15 text-benchmark-teal-bright",
  "return-shipped": "bg-accent-amber/15 text-accent-amber",
  closed: "bg-accent-green/15 text-accent-green",
};

export const WARRANTY_TONE: Record<RepairWarranty, string> = {
  "in-warranty": "bg-accent-green/15 text-accent-green",
  "extended-warranty": "bg-benchmark-teal/15 text-benchmark-teal-bright",
  "out-of-warranty": "bg-bg-elevated text-fg-secondary",
};
